import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';
import { toast } from 'sonner';

const CartContext = createContext();

// Storage keys for B2C and B2B carts
const B2C_CART_KEY = 'lart_cart';
const B2B_CART_KEY = 'b2b_cart';

// Helper to check if user is B2B logged in
// Checks for b2b_customer data (non-sensitive info stored in localStorage)
const isB2BUser = () => {
  const b2bCustomer = localStorage.getItem('b2b_customer');
  return !!b2bCustomer;
};

// Helper to get the correct cart storage key
const getCartStorageKey = () => {
  return isB2BUser() ? B2B_CART_KEY : B2C_CART_KEY;
};

// Helper to safely read from localStorage
const readCartFromStorage = (key = null) => {
  try {
    const storageKey = key || getCartStorageKey();
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    logger.error('Error reading cart from localStorage:', e);
  }
  return [];
};

// Helper to safely write to localStorage
const writeCartToStorage = (cart, key = null) => {
  try {
    const storageKey = key || getCartStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(cart));
  } catch (e) {
    logger.error('Error writing cart to localStorage:', e);
  }
};

// Helper to build a unique cart key (product + variant + personalization)
export const getCartItemKey = (item) => {
  const base = item.variant_id ? `${item.id}-${item.variant_id}` : item.id;
  const perso = item.personalization && (item.personalization.name || item.personalization.figure)
    ? `-perso-${item.personalization.name || ''}-${item.personalization.figure || ''}`
    : '';
  return base + perso;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Clear cart on every new site visit (page refresh/new tab)
  // Using sessionStorage to track if cart was already cleared this session
  useEffect(() => {
    const sessionKey = 'lart_session_active';
    const isNewSession = !sessionStorage.getItem(sessionKey);
    
    if (isNewSession) {
      // Clear both B2C and B2B carts on new session
      localStorage.removeItem(B2C_CART_KEY);
      localStorage.removeItem(B2B_CART_KEY);
      sessionStorage.setItem(sessionKey, 'true');
      logger.info('[CART] New session detected - carts cleared');
    }
  }, []);
  
  // Initialize state directly from localStorage based on current user type
  const [cart, setCart] = useState(() => readCartFromStorage());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartScope, setCartScope] = useState(() => isB2BUser() ? 'B2B' : 'B2C');
  const isInitialMount = useRef(true);
  const lastKnownKey = useRef(getCartStorageKey());
  
  // Function to reload cart from correct storage based on current user type
  const reloadCartFromStorage = useCallback(() => {
    const currentKey = getCartStorageKey();
    const currentScope = isB2BUser() ? 'B2B' : 'B2C';
    logger.info(`[CART] Reloading cart for scope: ${currentScope}, key: ${currentKey}`);
    lastKnownKey.current = currentKey;
    setCartScope(currentScope);
    setCart(readCartFromStorage(currentKey));
  }, []);
  
  // Check for B2B login state changes and switch cart accordingly
  useEffect(() => {
    const checkCartScope = () => {
      const currentKey = getCartStorageKey();
      
      if (currentKey !== lastKnownKey.current) {
        // User type changed (login/logout), reload cart from correct storage
        reloadCartFromStorage();
      }
    };
    
    // Check on mount and set up interval to detect B2B login changes
    checkCartScope();
    const interval = setInterval(checkCartScope, 300); // Faster polling
    
    // Also listen for custom cart-reload event (dispatched on B2B login/logout)
    const handleCartReload = () => {
      logger.info('[CART] Cart reload event received');
      reloadCartFromStorage();
    };
    window.addEventListener('cart-reload', handleCartReload);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('cart-reload', handleCartReload);
    };
  }, [reloadCartFromStorage]);
  
  // Sync cart to localStorage whenever it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    writeCartToStorage(cart);
  }, [cart]);
  
  // Also listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      const currentKey = getCartStorageKey();
      if (e.key === currentKey || e.key === B2B_CART_KEY || e.key === B2C_CART_KEY) {
        // Only update if the changed key matches our current scope
        if (e.key === currentKey) {
          const newCart = readCartFromStorage(currentKey);
          setCart(newCart);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  const addToCart = useCallback((product) => {
    setCart((prevCart) => {
      // Calculate effective price with discount
      let effectivePrice = product.price;
      if (product.discount_percentage && product.discount_percentage > 0) {
        effectivePrice = product.price * (1 - product.discount_percentage / 100);
        effectivePrice = Math.round(effectivePrice * 100) / 100;
      }

      // Add personalization surcharge (not subject to discount)
      if (product.personalization?.price) {
        effectivePrice = Math.round((effectivePrice + product.personalization.price) * 100) / 100;
      }

      // Create unique key for cart items (product + variant + personalization)
      const cartKey = getCartItemKey(product);
      
      const existingItem = prevCart.find(item => getCartItemKey(item) === cartKey);
      
      let newCart;
      if (existingItem) {
        const displayName = product.variant_name 
          ? `${product.name} (${product.variant_name})`
          : product.name;
        toast.success(`${displayName} toegevoegd (${existingItem.quantity + 1}x)`);
        newCart = prevCart.map(item => {
          return getCartItemKey(item) === cartKey
            ? { ...item, quantity: item.quantity + 1 }
            : item;
        });
      } else {
        const displayName = product.variant_name 
          ? `${product.name} (${product.variant_name})`
          : product.name;
        toast.success(`${displayName} toegevoegd aan winkelmandje`);
        newCart = [...prevCart, { 
          ...product, 
          price: effectivePrice,
          original_price: product.price,
          discount_percentage: product.discount_percentage || 0,
          quantity: 1,
          // Ensure variant fields are preserved
          variant_id: product.variant_id || null,
          variant_name: product.variant_name || null,
          color_name: product.color_name || null,
          color_hex: product.color_hex || null,
          // Add-on & personalization
          personalization: product.personalization || null,
          is_addon: product.is_addon || false,
        }];
      }
      
      // Write to storage immediately for reliability
      writeCartToStorage(newCart);
      return newCart;
    });
    
    setIsCartOpen(true);
  }, []);
  
  const removeFromCart = useCallback((productId) => {
    setCart((prevCart) => {
      // Support both simple productId and full cart item key
      const newCart = prevCart.filter(item => {
        const itemKey = getCartItemKey(item);
        return itemKey !== productId && item.id !== productId;
      });
      writeCartToStorage(newCart);
      return newCart;
    });
    toast.success('Verwijderd uit winkelmandje');
  }, []);
  
  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart((prevCart) => {
      const newCart = prevCart.map(item => {
        const itemKey = getCartItemKey(item);
        return itemKey === productId 
          ? { ...item, quantity } 
          : item;
      });
      writeCartToStorage(newCart);
      return newCart;
    });
  }, [removeFromCart]);
  
  const clearCart = useCallback(() => {
    setCart([]);
    writeCartToStorage([]);
    toast.success('Winkelmandje geleegd');
  }, []);
  
  // For B2B, use b2b_price; for B2C, use price
  const getCartTotal = useCallback(() => {
    const isB2B = isB2BUser();
    return cart.reduce((total, item) => {
      const price = isB2B ? (item.b2b_price || item.price || 0) : (item.price || 0);
      return total + (price * item.quantity);
    }, 0);
  }, [cart]);
  
  const getCartCount = useCallback(() => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);
  
  // Refresh cart from storage (useful after B2B login/logout)
  const refreshCart = useCallback(() => {
    const newCart = readCartFromStorage();
    setCart(newCart);
    setCartScope(isB2BUser() ? 'B2B' : 'B2C');
  }, []);
  
  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        isCartOpen,
        setIsCartOpen,
        cartScope,
        refreshCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
